import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, CheckCircle2 } from "lucide-react";

/**
 * VAULT Brief · v2 — signature hero intelligent summary.
 *
 * MIDNIGHT LUXE styling. Reads counts from the same payloads Home.js
 * already fetches — never asks the server for anything new. This
 * keeps Phase 1 pure UI and zero backend risk.
 *
 * Props:
 *   greetingName  — user's first name (empty falls back to "there")
 *   counts        — { payments_due, subs_renewing, docs_attention, overdue }
 *   window        — one of: "today" | "week" | "month"
 *   onWindowChange
 */
const WINDOWS = [
    { key: "today", label: "Today" },
    { key: "week",  label: "This Week" },
    { key: "month", label: "This Month" },
];

function greeting() {
    const h = new Date().getHours();
    if (h < 5)  return "Late evening";
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    if (h < 21) return "Good evening";
    return "Late evening";
}

export default function VaultBriefV2({
    greetingName = "",
    counts = {},
    window: activeWindow = "week",
    onWindowChange,
}) {
    const [win, setWin] = useState(activeWindow);
    const name = (greetingName || "").split(" ")[0] || "there";

    const rows = useMemo(() => {
        const items = [];
        const pay  = counts.payments_due ?? 0;
        const subs = counts.subs_renewing ?? 0;
        const docs = counts.docs_attention ?? 0;
        const over = counts.overdue ?? 0;

        if (over > 0) {
            items.push({
                key: "overdue",
                count: over,
                label: over === 1 ? "overdue obligation" : "overdue obligations",
                tone: "urgent",
                to: "/payments",
            });
        }
        if (pay > 0) {
            items.push({
                key: "pay",
                count: pay,
                label: pay === 1 ? "payment coming up" : "payments coming up",
                tone: pay > 3 ? "attention" : "neutral",
                to: "/payments",
            });
        }
        if (subs > 0) {
            items.push({
                key: "subs",
                count: subs,
                label: subs === 1 ? "subscription renewing" : "subscriptions renewing",
                tone: "neutral",
                to: "/subscriptions",
            });
        }
        if (docs > 0) {
            items.push({
                key: "docs",
                count: docs,
                label: docs === 1 ? "document needs attention" : "documents need attention",
                tone: "attention",
                to: "/documents",
            });
        }
        if (over === 0) {
            items.push({
                key: "clear",
                count: null,
                label: "No overdue obligations",
                tone: "check",
                to: "/finance-review",
            });
        }
        return items;
    }, [counts]);

    const handleChange = (key) => {
        setWin(key);
        onWindowChange && onWindowChange(key);
    };

    return (
        <div className="v2-hero" data-testid="v2-vault-brief">
            <div className="v2-greeting">
                <div>
                    <span className="v2-preview-tag" data-testid="v2-preview-tag">
                        VAULT 2.0 · Founder Preview
                    </span>
                    <h2 className="v2-greeting__hello mt-3" data-testid="v2-brief-hello">
                        {greeting()}, {name}.
                    </h2>
                    <p className="v2-greeting__lede">
                        Here&rsquo;s what needs your attention.
                    </p>
                </div>
                <div
                    className="v2-chips"
                    role="tablist"
                    aria-label="Brief window"
                    data-testid="v2-brief-window"
                >
                    {WINDOWS.map((w) => (
                        <button
                            key={w.key}
                            type="button"
                            role="tab"
                            aria-selected={win === w.key}
                            className={`v2-chip ${win === w.key ? "is-active" : ""}`}
                            onClick={() => handleChange(w.key)}
                            data-testid={`v2-brief-window-${w.key}`}
                        >
                            {w.label}
                        </button>
                    ))}
                </div>
            </div>

            <div className="mt-2" data-testid="v2-brief-rows">
                {rows.map((r) => (
                    <Link
                        key={r.key}
                        to={r.to}
                        className={`v2-brief-row v2-brief-row--${r.tone}`}
                        data-testid={`v2-brief-row-${r.key}`}
                    >
                        <span className="v2-brief-row__count v2-num">
                            {r.count === null ? <CheckCircle2 size={30} strokeWidth={1.6} /> : r.count}
                        </span>
                        <span className="v2-brief-row__label">{r.label}</span>
                        <span className="v2-brief-row__cta">
                            Open <ArrowRight size={12} className="inline -mt-[1px]" />
                        </span>
                    </Link>
                ))}
            </div>

            <div className="mt-6 flex items-center gap-3">
                <Link to="/finance-review" className="v2-btn v2-btn--gold" data-testid="v2-brief-cta-primary">
                    <Sparkles size={14} strokeWidth={2} />
                    Open VAULT Brief
                </Link>
                <Link to="/calendar" className="v2-btn v2-btn--ghost" data-testid="v2-brief-cta-secondary">
                    View calendar
                </Link>
            </div>
        </div>
    );
}
